import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useReportBuilderList } from '../hooks/useReportBuilderList';
import { useDeleteReportDef } from '../hooks/useDeleteReportDef';
import { useGenerateReport } from '../hooks/useGenerateReport';
import { ReportPreviewDrawer } from '../components/ReportPreviewDrawer';
import { ReportCommonUsagePanel } from '../components/ReportCommonUsagePanel';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { StatusTone } from '@ui-kit';
import type { ReportListItem, ApiError, ReportDefinitionStatus } from '../types/reportBuilder';

const DEFAULT_PAGE_SIZE = 20;

function statusTone(s: ReportDefinitionStatus): StatusTone {
  if (s === 'ACTIVE') return 'success';
  if (s === 'ARCHIVED') return 'warning';
  return 'neutral';
}


/** Skeleton 행 */
function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i * 13) % 40}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * ReportListPage — 리포트 정의 목록 (/report-builder)
 * 5상태: loading / empty / error / default / dialog(삭제확인 + 포맷선택 + 미리보기)
 */
export function ReportListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // 필터 상태
  const [nameFilter, setNameFilter] = useState('');
  const [dataSourceFilter, setDataSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerScope, setOwnerScope] = useState<'mine' | 'all'>('mine');
  const [page, setPage] = useState(0);

  // 적용된 필터 (검색 버튼 클릭 시 반영)
  const [appliedFilters, setAppliedFilters] = useState({
    name: '',
    dataSource: '',
    status: '',
    ownerScope: 'mine' as 'mine' | 'all',
    page: 0,
  });

  // 삭제 confirm dialog
  const [deleteTarget, setDeleteTarget] = useState<ReportListItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // 포맷 선택 dropdown
  const [generateTarget, setGenerateTarget] = useState<ReportListItem | null>(null);
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);

  // 미리보기 drawer
  const [previewTarget, setPreviewTarget] = useState<ReportListItem | null>(null);

  const listQuery = useReportBuilderList({
    name: appliedFilters.name || undefined,
    dataSource: appliedFilters.dataSource || undefined,
    status: (appliedFilters.status as ReportDefinitionStatus) || undefined,
    ownerScope: appliedFilters.ownerScope,
    page: appliedFilters.page,
    size: DEFAULT_PAGE_SIZE,
    sort: 'createdAt,desc',
  });

  const deleteMutation = useDeleteReportDef();
  const { doGenerate, isPending: isGenerating, errorCode: generateError } = useGenerateReport();

  const pageData = listQuery.data;
  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && (pageData?.empty ?? false);
  const showTable = !isListLoading && !isListError && !isEmpty && !!pageData;

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleSearch = useCallback(() => {
    setPage(0);
    setAppliedFilters({ name: nameFilter, dataSource: dataSourceFilter, status: statusFilter, ownerScope, page: 0 });
  }, [nameFilter, dataSourceFilter, statusFilter, ownerScope]);

  const handleReset = useCallback(() => {
    setNameFilter('');
    setDataSourceFilter('');
    setStatusFilter('');
    setOwnerScope('mine');
    setPage(0);
    setAppliedFilters({ name: '', dataSource: '', status: '', ownerScope: 'mine', page: 0 });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast(t('reportBuilder.toast.deleted'));
      },
      onError: (err) => {
        setDeleteTarget(null);
        const apiErr = err as unknown as ApiError;
        showToast(
          t(`reportBuilder.error.${apiErr.code}`, { defaultValue: t('reportBuilder.error.network') })
        );
      },
    });
  }, [deleteTarget, deleteMutation, showToast, t]);

  const handleGenerateFormat = useCallback(
    async (item: ReportListItem, format: 'excel' | 'pdf') => {
      setFormatMenuOpen(false);
      setGenerateTarget(null);
      showToast(t('reportBuilder.toast.generateStarted'));
      await doGenerate(item.id, format);
      if (generateError) {
        showToast(
          t(`reportBuilder.error.${generateError}`, {
            defaultValue: t('reportBuilder.toast.generateFailed'),
          })
        );
      }
    },
    [doGenerate, generateError, showToast, t]
  );

  const TABLE_HEADERS = useMemo(
    () => [
      t('reportBuilder.list.column.name'),
      t('reportBuilder.list.column.dataSource'),
      t('reportBuilder.list.column.format'),
      t('reportBuilder.list.column.columns'),
      t('reportBuilder.list.column.owner'),
      t('reportBuilder.list.column.status'),
      t('reportBuilder.list.column.actions'),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('reportBuilder.list.title')}
        breadcrumb={<span>{t('reportBuilder.list.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/report-builder/history')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('reportBuilder.list.button.history')}
            </button>
            <ButtonGuard requirePermission="REPORT_CREATE">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  disabled={disabled}
                  title={reason}
                  onClick={() => navigate('/report-builder/new')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  + {t('reportBuilder.list.button.new')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* 요약 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {pageData.totalElements > 0
            ? t('reportBuilder.list.totalCount', { count: pageData.totalElements })
            : t('reportBuilder.list.noResults')}
        </p>
      )}

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('reportBuilder.list.title')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'flex-end',
        }}
      >
        {/* 이름 */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('reportBuilder.list.filter.name')}
          <input
            type="text"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder={t('reportBuilder.list.filter.namePlaceholder')}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
              minWidth: 160,
            }}
          />
        </label>

        {/* 데이터 소스 */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('reportBuilder.list.filter.dataSource')}
          <select
            value={dataSourceFilter}
            onChange={(e) => setDataSourceFilter(e.target.value)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('reportBuilder.dataSource.all')}</option>
            <option value="user">{t('reportBuilder.dataSource.user')}</option>
            <option value="audit-log">{t('reportBuilder.dataSource.audit-log')}</option>
            <option value="static">{t('reportBuilder.dataSource.static')}</option>
          </select>
        </label>

        {/* 상태 */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('reportBuilder.list.filter.status')}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('reportBuilder.dataSource.all')}</option>
            <option value="DRAFT">{t('reportBuilder.status.DRAFT')}</option>
            <option value="ACTIVE">{t('reportBuilder.status.ACTIVE')}</option>
            <option value="ARCHIVED">{t('reportBuilder.status.ARCHIVED')}</option>
          </select>
        </label>

        {/* 범위 */}
        <fieldset
          style={{
            border: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
          }}
        >
          <legend style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
            {t('reportBuilder.list.filter.status')}
          </legend>
          <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
              <input
                type="radio"
                name="ownerScope"
                value="mine"
                checked={ownerScope === 'mine'}
                onChange={() => setOwnerScope('mine')}
              />
              {t('reportBuilder.list.filter.scopeMine')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
              <input
                type="radio"
                name="ownerScope"
                value="all"
                checked={ownerScope === 'all'}
                onChange={() => setOwnerScope('all')}
              />
              {t('reportBuilder.list.filter.scopeAll')}
            </label>
          </div>
        </fieldset>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('reportBuilder.list.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('reportBuilder.list.button.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('reportBuilder.list.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('reportBuilder.list.error.loadFailed')}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('reportBuilder.list.error.forbidden')
                  : ((listQuery.error as unknown as ApiError)?.message ??
                    t('reportBuilder.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('reportBuilder.list.empty.title')}
              description={t('reportBuilder.list.empty.description')}
              action={
                <button
                  type="button"
                  onClick={() => navigate('/report-builder/new')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  + {t('reportBuilder.list.button.new')}
                </button>
              }
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            <div
              role="region"
              aria-label={t('reportBuilder.list.tableAriaLabel')}
              style={{ overflowX: 'auto' }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label={t('reportBuilder.list.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      aria-label={t('reportBuilder.list.rowAriaLabel', { id: row.id })}
                      style={{ transition: 'background 0.1s' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      {/* 이름 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        {row.name}
                        {row.shared && (
                          <span
                            style={{
                              marginLeft: 'var(--spacing-2)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            [공유]
                          </span>
                        )}
                      </td>
                      {/* 데이터 소스 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-secondary)',
                          fontFamily: 'var(--font-family-mono)',
                        }}
                      >
                        {t(`reportBuilder.dataSource.${row.dataSource}`, { defaultValue: row.dataSource })}
                      </td>
                      {/* 포맷 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        {t(`reportBuilder.format.${row.defaultFormat}`, { defaultValue: row.defaultFormat })}
                      </td>
                      {/* 컬럼 수 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-secondary)',
                          textAlign: 'center',
                        }}
                      >
                        {row.columnCount}
                      </td>
                      {/* 소유자 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        {row.ownerName ?? row.owner}
                      </td>
                      {/* 상태 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                        }}
                      >
                        <StatusPill tone={statusTone(row.status)}>
                          {t(`reportBuilder.status.${row.status}`)}
                        </StatusPill>
                      </td>
                      {/* 액션 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <div style={{ display: 'flex', gap: 'var(--spacing-1)', alignItems: 'center', flexWrap: 'nowrap' }}>
                          {/* 생성 드롭다운 */}
                          <div style={{ position: 'relative' }}>
                            <ButtonGuard requirePermission="REPORT_GENERATE">
                              {({ disabled, reason }) => (
                                <button
                                  type="button"
                                  disabled={disabled || isGenerating(row.id, row.defaultFormat.toLowerCase() as 'excel' | 'pdf')}
                                  title={reason}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGenerateTarget(row);
                                    setFormatMenuOpen(true);
                                  }}
                                  style={{
                                    padding: 'var(--spacing-1) var(--spacing-2)',
                                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                                    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 600,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    fontFamily: 'var(--font-family-sans)',
                                    opacity: disabled ? 0.6 : 1,
                                  }}
                                >
                                  {isGenerating(row.id, row.defaultFormat.toLowerCase() as 'excel' | 'pdf')
                                    ? t('reportBuilder.generate.pending')
                                    : t('reportBuilder.list.button.generate') + ' ▼'}
                                </button>
                              )}
                            </ButtonGuard>

                            {/* 포맷 선택 드롭다운 메뉴 (dialog 상태) */}
                            {formatMenuOpen && generateTarget?.id === row.id && (
                              <div
                                role="menu"
                                aria-label={t('reportBuilder.generate.formatSelect.title')}
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  zIndex: 500,
                                  background: 'var(--color-surface-background)',
                                  border: '1px solid var(--color-surface-border)',
                                  borderRadius: 'var(--radius-md)',
                                  boxShadow: 'var(--shadow-lg)',
                                  minWidth: 140,
                                  marginTop: 4,
                                }}
                              >
                                {(['excel', 'pdf'] as const).map((fmt) => (
                                  <button
                                    key={fmt}
                                    type="button"
                                    role="menuitem"
                                    onClick={() => void handleGenerateFormat(row, fmt)}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: 'var(--spacing-2) var(--spacing-3)',
                                      background: 'transparent',
                                      border: 'none',
                                      textAlign: 'left',
                                      fontSize: 'var(--font-size-sm)',
                                      color: 'var(--color-text-primary)',
                                      cursor: 'pointer',
                                      fontFamily: 'var(--font-family-sans)',
                                    }}
                                    onMouseEnter={(e) => {
                                      (e.currentTarget as HTMLButtonElement).style.background =
                                        'var(--color-surface-muted)';
                                    }}
                                    onMouseLeave={(e) => {
                                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                                    }}
                                  >
                                    {t(`reportBuilder.generate.formatSelect.${fmt}`)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* 미리보기 */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewTarget(row);
                            }}
                            style={{
                              padding: 'var(--spacing-1) var(--spacing-2)',
                              background: 'transparent',
                              border: '1px solid var(--color-surface-border)',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-text-secondary)',
                              cursor: 'pointer',
                              fontFamily: 'var(--font-family-sans)',
                            }}
                          >
                            {t('reportBuilder.list.button.preview')}
                          </button>

                          {/* 편집 */}
                          <ButtonGuard requirePermission="REPORT_CREATE">
                            {({ disabled, reason }) => (
                              <button
                                type="button"
                                disabled={disabled}
                                title={reason}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/report-builder/${row.id}/edit`);
                                }}
                                style={{
                                  padding: 'var(--spacing-1) var(--spacing-2)',
                                  background: 'transparent',
                                  border: '1px solid var(--color-surface-border)',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: 'var(--font-size-xs)',
                                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                  fontFamily: 'var(--font-family-sans)',
                                  opacity: disabled ? 0.5 : 1,
                                }}
                              >
                                {t('reportBuilder.list.button.edit')}
                              </button>
                            )}
                          </ButtonGuard>

                          {/* 삭제 */}
                          <ButtonGuard requirePermission="REPORT_CREATE">
                            {({ disabled, reason }) => (
                              <button
                                type="button"
                                disabled={disabled}
                                title={reason}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget(row);
                                }}
                                style={{
                                  padding: 'var(--spacing-1) var(--spacing-2)',
                                  background: 'transparent',
                                  border: `1px solid ${disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: 'var(--font-size-xs)',
                                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-danger)',
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                  fontFamily: 'var(--font-family-sans)',
                                  opacity: disabled ? 0.5 : 1,
                                }}
                              >
                                {t('reportBuilder.list.button.delete')}
                              </button>
                            )}
                          </ButtonGuard>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
              >
                {t('reportBuilder.list.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav
                aria-label={t('reportBuilder.list.pagination.nav')}
                style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}
              >
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('reportBuilder.list.pagination.prev')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('reportBuilder.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background: i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                      border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                      color: i === pageData.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('reportBuilder.list.pagination.next')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* 공통 모듈 패널 */}
      <ReportCommonUsagePanel />

      {/* 미리보기 Drawer (dialog 상태) */}
      {previewTarget && (
        <ReportPreviewDrawer
          reportId={previewTarget.id}
          reportName={previewTarget.name}
          onClose={() => setPreviewTarget(null)}
        />
      )}

      {/* 삭제 confirm dialog (dialog 상태) */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
          onKeyDown={(e) => { if (e.key === 'Escape') setDeleteTarget(null); }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 420,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="delete-dialog-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-3)' }}
            >
              {t('reportBuilder.confirm.delete.title')}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-5)',
              }}
            >
              {t('reportBuilder.confirm.delete.body')}
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-5)',
              }}
            >
              "{deleteTarget.name}"
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('reportBuilder.confirm.delete.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
                autoFocus
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: deleteMutation.isPending ? 0.6 : 1,
                }}
              >
                {deleteMutation.isPending ? '...' : t('reportBuilder.confirm.delete.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 포맷 메뉴 외부 클릭 닫기 오버레이 */}
      {formatMenuOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 499 }}
          onClick={() => { setFormatMenuOpen(false); setGenerateTarget(null); }}
          aria-hidden="true"
        />
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      {/* 반응형 스타일 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 600px; }
        }
      `}</style>
    </div>
  );
}
